// One task: plan it in a block, or send it to the list, Ooit or away; duration, deadline, note and steps (spec T2).
import { useEffect, useState, useSyncExternalStore, type FormEvent } from "react";
import { aiError, aiSplit, useAiEnabled } from "../lib/ai";
import { CategoryChoice } from "./CategoryChoice";
import { useLiveQuery } from "dexie-react-hooks";
import { Archive, CalendarPlus, Check, ListTodo, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { displayDate, displayTime, timeToMinutes } from "@shared/time";
import { db, type Task } from "../lib/db";
import {
  addStep,
  planTask,
  removeStep,
  restoreEntries,
  setStepChecked,
  setTaskStatus,
  updateTask,
  keepTask,
  type PlanReason,
  type PlanTarget,
} from "../lib/tasks";
import { PlanPicker } from "./PlanPicker";
import { Sheet } from "./Sheet";
import { Button, cn } from "./ui";

// Which task is open: a tiny store, so any screen can open the sheet (optionally straight in the plan picker).
interface OpenState {
  id: string;
  plan: boolean;
  reason: PlanReason;
}
let openState: OpenState | null = null;
const listeners = new Set<() => void>();
export function openTask(id: string | null, options: { plan?: boolean; reason?: PlanReason } = {}) {
  openState = id ? { id, plan: options.plan ?? false, reason: options.reason ?? "planned" } : null;
  listeners.forEach((l) => l());
}
function useOpenTask() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => openState,
  );
}

const DURATIONS: { minutes: 15 | 30 | 60; label: string }[] = [
  { minutes: 15, label: "Kort" },
  { minutes: 30, label: "Middel" },
  { minutes: 60, label: "Lang" },
];

const STATUS_LABEL: Record<Task["status"], string> = {
  inbox: "Nieuw",
  open: "Te doen, zonder dag",
  someday: "In Ooit",
  done: "Klaar",
  cancelled: "Weggezet",
};

export function TaskSheetHost({ dayStart }: { dayStart: string }) {
  const state = useOpenTask();
  return state ? (
    <TaskSheet key={state.id} id={state.id} startPlanning={state.plan} reason={state.reason} dayStart={timeToMinutes(dayStart)} />
  ) : null;
}

function TaskSheet({ id, startPlanning, reason, dayStart }: { id: string; startPlanning: boolean; reason: PlanReason; dayStart: number }) {
  const data = useLiveQuery(async () => {
    const task = await db.tasks.get(id);
    if (!task) return null;
    const entry = await db.plan_entries
      .where("task_id")
      .equals(id)
      .filter((e) => e.status === "planned" && !e.deleted_at)
      .first();
    const block = entry?.day_block_id
      ? await db.day_blocks.get(entry.day_block_id)
      : entry?.pending_template_block_id
        ? await db.day_template_blocks.get(entry.pending_template_block_id)
        : undefined;
    const steps = (await db.task_steps.where("task_id").equals(id).toArray())
      .filter((s) => !s.deleted_at)
      .sort((a, b) => a.position - b.position);
    // Where it stood before (spec N1 AC5: the original day stays visible).
    const from = entry?.carried_from_id ? await db.plan_entries.get(entry.carried_from_id) : undefined;
    return { task, entry, block, steps, movedFrom: from?.plan_date };
  }, [id]);
  const [planning, setPlanning] = useState(startPlanning);
  const [step, setStep] = useState("");

  // The task was removed (for example on another device): close the sheet.
  useEffect(() => {
    if (data === null) openTask(null);
  }, [data]);

  if (!data) return null;
  const { task, entry, block, steps, movedFrom } = data;
  const close = () => openTask(null);

  async function plan(target: PlanTarget) {
    const ok = await planTask(id, target, reason);
    setPlanning(false);
    toast(ok ? `Ingepland op ${displayDate(target.date)}` : "Dat kan niet: die dag is voorbij of de taak is al klaar.");
    close();
  }

  async function moveTo(status: Task["status"], message: string) {
    const previous = task.status;
    const entryId = entry?.id;
    await setTaskStatus(id, status);
    toast(message, {
      duration: status === "cancelled" ? 10_000 : 5000,
      action: {
        label: "Ongedaan maken",
        onClick: async () => {
          await setTaskStatus(id, previous);
          if (entryId) await restoreEntries([entryId]);
        },
      },
    });
    close();
  }

  async function submitStep(event: FormEvent) {
    event.preventDefault();
    if (!step.trim()) return;
    await addStep(id, step, steps.length);
    setStep("");
  }

  const where = entry
    ? `Gepland op ${displayDate(entry.plan_date)}${block ? `, ${block.title} (${displayTime(block.start_local)})` : ""}`
    : STATUS_LABEL[task.status];

  return (
    <Sheet open onOpenChange={(o) => !o && close()} title={planning ? "Inplannen" : "Taak"}>
      {planning ? (
        <div className="flex flex-col gap-3">
          <PlanPicker dayStart={dayStart} onPick={plan} />
          <Button variant="secondary" onClick={() => setPlanning(false)}>
            Terug
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div>
            <label htmlFor="task-title" className="sr-only">
              Titel
            </label>
            <textarea
              id="task-title"
              defaultValue={task.title}
              rows={2}
              maxLength={500}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (value && value !== task.title) void updateTask(id, { title: value });
              }}
              className="w-full resize-none rounded-xl border-2 border-transparent bg-transparent px-1 text-xl font-semibold text-ink [overflow-wrap:anywhere] focus:border-mist"
            />
            <p className="px-1 text-ink-soft">{where}</p>
            {movedFrom && <p className="px-1 text-sm text-ink-soft">Stond eerst op {displayDate(movedFrom)}</p>}
          </div>

          {task.status !== "done" && (
            <div className="grid grid-cols-2 gap-2">
              <Button className="col-span-2" onClick={() => setPlanning(true)}>
                <CalendarPlus aria-hidden className="size-5" />
                {entry ? "Andere dag of blok" : "Inplannen"}
              </Button>
              <Button variant="secondary" onClick={() => moveTo("done", "Afgevinkt")}>
                <Check aria-hidden className="size-5" />
                Klaar
              </Button>
              {task.status !== "open" || entry ? (
                <Button variant="secondary" onClick={() => moveTo("open", "Bij Te doen, zonder dag")}>
                  <ListTodo aria-hidden className="size-5" />
                  Zonder dag
                </Button>
              ) : null}
              {task.status !== "someday" && (
                <Button variant="secondary" onClick={() => moveTo("someday", "Naar Ooit gezet")}>
                  <Archive aria-hidden className="size-5" />
                  Ooit
                </Button>
              )}
              <Button variant="secondary" onClick={() => moveTo("cancelled", "Weggezet")}>
                <Trash2 aria-hidden className="size-5" />
                Weg
              </Button>
            </div>
          )}
          {task.status === "done" && (
            <Button variant="secondary" onClick={() => moveTo("open", "Weer bij Te doen")}>
              Toch niet klaar
            </Button>
          )}

          <CategoryChoice
            value={task.category_id ?? null}
            onChange={(categoryId) =>
              void (task.status === "inbox" && categoryId ? keepTask(id, categoryId) : updateTask(id, { category_id: categoryId }))
            }
          />

          <ReminderField value={task.remind_at ?? null} onChange={(remindAt) => void updateTask(id, { remind_at: remindAt })} />

          <fieldset>
            <legend className="mb-2 font-semibold">Duur</legend>
            <div className="flex gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d.minutes}
                  type="button"
                  aria-pressed={task.estimate_minutes === d.minutes}
                  onClick={() => void updateTask(id, { estimate_minutes: task.estimate_minutes === d.minutes ? null : d.minutes })}
                  className={cn(
                    "min-h-12 flex-1 rounded-xl border-2 px-2",
                    task.estimate_minutes === d.minutes ? "border-pine bg-pine text-paper" : "border-mist",
                  )}
                >
                  {d.label}
                  <span className="block text-sm opacity-80">{d.minutes === 60 ? "1 uur" : `${d.minutes} min`}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <div className="flex items-end gap-2">
            <label className="flex flex-1 flex-col gap-1">
              <span className="font-semibold">Deadline</span>
              <input
                type="date"
                value={task.due_on ?? ""}
                onChange={(e) => void updateTask(id, { due_on: e.target.value || null })}
                className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
              />
            </label>
            {task.due_on && (
              <button
                type="button"
                onClick={() => void updateTask(id, { due_on: null })}
                aria-label="Deadline weghalen"
                className="flex size-12 items-center justify-center rounded-xl border-2 border-mist"
              >
                <X aria-hidden className="size-5" />
              </button>
            )}
          </div>

          <label className="flex flex-col gap-1">
            <span className="font-semibold">Notitie</span>
            <textarea
              defaultValue={task.notes ?? ""}
              rows={3}
              maxLength={10000}
              onBlur={(e) => {
                const value = e.target.value.trim() || null;
                if (value !== task.notes) void updateTask(id, { notes: value });
              }}
              className="rounded-xl border-2 border-mist bg-paper px-3 py-2 text-ink [overflow-wrap:anywhere]"
            />
          </label>

          <section aria-labelledby="steps">
            <h3 id="steps" className="mb-1 font-semibold">
              Stappen
            </h3>
            <ul>
              {steps.map((s) => (
                <li key={s.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={!!s.checked_at}
                    onClick={() => void setStepChecked(s.id, !s.checked_at)}
                    className="flex min-h-12 flex-1 items-center gap-3 rounded-xl px-1 text-left"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex size-6 shrink-0 items-center justify-center rounded-md border-2",
                        s.checked_at ? "border-pine bg-pine text-paper" : "border-ink-soft",
                      )}
                    >
                      {s.checked_at && <Check className="size-4" strokeWidth={3} />}
                    </span>
                    <span className={cn("min-w-0 [overflow-wrap:anywhere]", s.checked_at && "text-done line-through")}>{s.title}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void removeStep(s.id)}
                    aria-label={`Stap ${s.title} weghalen`}
                    className="flex size-11 items-center justify-center rounded-xl text-ink-soft"
                  >
                    <X aria-hidden className="size-5" />
                  </button>
                </li>
              ))}
            </ul>
            <SplitWithAi taskId={id} count={steps.length} />
            <form onSubmit={submitStep} className="mt-1 flex gap-2">
              <label htmlFor="new-step" className="sr-only">
                Nieuwe stap
              </label>
              <input
                id="new-step"
                value={step}
                maxLength={300}
                onChange={(e) => setStep(e.target.value)}
                placeholder="Stap toevoegen"
                className="min-h-12 flex-1 rounded-xl border-2 border-mist bg-paper px-3 text-ink placeholder:text-ink-soft"
              />
              <Button type="submit" variant="secondary" disabled={!step.trim()}>
                Toevoegen
              </Button>
            </form>
          </section>
        </div>
      )}
    </Sheet>
  );
}

/** Opdelen (AI golf B): a proposal of small steps; PK takes them over with one tap, or not. */
function SplitWithAi({ taskId, count }: { taskId: string; count: number }) {
  const ai = useAiEnabled();
  const [proposal, setProposal] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  if (!ai) return null;
  if (proposal)
    return (
      <div className="mt-2 rounded-xl border-2 border-sun p-3">
        <p className="mb-1 font-semibold">Voorstel</p>
        <ol className="mb-3 list-decimal pl-5">
          {proposal.map((s) => (
            <li key={s} className="[overflow-wrap:anywhere]">
              {s}
            </li>
          ))}
        </ol>
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={async () => {
              for (const [i, title] of proposal.entries()) await addStep(taskId, title, count + i);
              setProposal(null);
            }}
          >
            Overnemen
          </Button>
          <Button variant="secondary" onClick={() => setProposal(null)}>
            Niet nodig
          </Button>
        </div>
      </div>
    );
  return (
    <Button
      variant="quiet"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          setProposal((await aiSplit(taskId)).steps);
        } catch (error) {
          toast(aiError(error, "Opdelen"));
        } finally {
          setBusy(false);
        }
      }}
    >
      <Sparkles aria-hidden className="size-5" />
      {busy ? "Bezig met opdelen" : "Opdelen in kleine stappen"}
    </Button>
  );
}

/** A reminder: the task rings at this moment (AI golf A); a local clock time, kept as a moment. */
function ReminderField({ value, onChange }: { value: string | null; onChange: (value: string | null) => void }) {
  const local = value ? new Date(new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : "";
  return (
    <div className="flex items-end gap-2">
      <label className="flex flex-1 flex-col gap-1">
        <span className="font-semibold">Herinnering</span>
        <input
          type="datetime-local"
          value={local}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
          className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
        />
      </label>
      {value && (
        <Button variant="quiet" onClick={() => onChange(null)} aria-label="Herinnering weghalen">
          Weg
        </Button>
      )}
    </div>
  );
}
